import { useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Loader2, RefreshCw, Zap } from "lucide-react";
import { useTran } from "@/hooks";
import {
  useDisclosureStore,
  useEntryUIStore,
} from "../../hooks/useDataEntryStore";
import { syncFromNxmap } from "../../operations/entry.ops";
import { dataEntryService } from "../../services/data-entry.services";
import { NxmapSyncForm } from "./NxmapSyncForm";
import { NxmapSyncPreview } from "./NxmapSyncPreview";

const NxmapImportDialog = observer(() => {
  const { t } = useTran();
  const { activeDisclosure } = useDisclosureStore();
  const framework = activeDisclosure?.framework || "";
  const { showNxmapImportDialog, creatingEntries, setShowNxmapImportDialog } =
    useEntryUIStore();

  const masterDataYears = activeDisclosure?.masterDataYears || [];

  const [organizationId, setOrganizationId] = useState("all");
  const [selectedYear, setSelectedYear] = useState("");
  const [state, setState] = useState("idle");
  const [preview, setPreview] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const disclosureCode = activeDisclosure?.code;
  const summary = preview?.summary;
  const hasChanges = summary && (summary.new > 0 || summary.updated > 0);

  const canFetch =
    organizationId && organizationId !== "all" && selectedYear && framework;

  const fetchPreview = useCallback(async () => {
    setState("loading");
    setErrorMsg("");
    try {
      const result = await dataEntryService.syncNxmapEntries({
        framework,
        year: Number(selectedYear),
        orgId: organizationId,
        disclosureId: activeDisclosure?._id,
        confirm: false,
      });
      setPreview(result);
      setState("preview");
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch NXMap emissions. Please try again.",
      );
      setState("error");
    }
  }, [framework, selectedYear, organizationId, activeDisclosure?._id]);

  const handleClose = (open) => {
    if (!open) {
      setOrganizationId("all");
      setSelectedYear("");
      setState("idle");
      setPreview(null);
      setErrorMsg("");
    }
    setShowNxmapImportDialog(open);
  };

  const handleConfirm = async () => {
    if (!hasChanges) return;
    setState("syncing");
    const result = await syncFromNxmap({
      framework,
      year: Number(selectedYear),
      orgId: organizationId,
      disclosureId: activeDisclosure?._id,
    });
    if (result) {
      handleClose(false);
    } else {
      setState("preview");
    }
  };

  return (
    <Dialog open={showNxmapImportDialog} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap size={16} className="text-primary" />
            {t("Sync from NXMap")}
            {disclosureCode && (
              <Badge variant="outline" className="font-mono text-xs">
                {disclosureCode}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Phase 1: Selection */}
        {(state === "idle" || state === "loading" || state === "error") && (
          <NxmapSyncForm
            organizationId={organizationId}
            setOrganizationId={setOrganizationId}
            masterDataYears={masterDataYears}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
          />
        )}

        {/* Loading */}
        {state === "loading" && (
          <div className="flex flex-col items-center gap-3 py-6 text-muted-foreground">
            <Loader2 size={28} className="animate-spin text-primary" />
            <p className="text-sm">{t("Fetching emissions from NXMap...")}</p>
          </div>
        )}

        {/* Error */}
        {state === "error" && (
          <div className="flex flex-col items-center gap-3 py-4 text-destructive">
            <AlertCircle size={28} />
            <p className="text-sm text-center">{errorMsg}</p>
          </div>
        )}

        {/* Phase 2: Preview */}
        {(state === "preview" || state === "syncing") && preview && (
          <NxmapSyncPreview preview={preview} disclosureCode={disclosureCode} />
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={state === "loading" || state === "syncing"}
          >
            {t("Cancel")}
          </Button>

          {(state === "idle" || state === "error") && (
            <Button disabled={!canFetch} onClick={fetchPreview}>
              <Zap size={14} />
              {t("Fetch Preview")}
            </Button>
          )}

          {(state === "preview" || state === "syncing") && hasChanges && (
            <Button
              onClick={handleConfirm}
              disabled={state === "syncing" || creatingEntries}
            >
              {state === "syncing" ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <RefreshCw size={14} />
              )}
              {summary?.updated > 0 ? t("Sync") : t("Import")}
            </Button>
          )}

          {state === "preview" && !hasChanges && (
            <Button
              variant="outline"
              onClick={() => {
                setState("idle");
                setPreview(null);
              }}
            >
              {t("Back")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

export default NxmapImportDialog;
